/**
 * Pure helpers for the progression views and the dashboard command center.
 *
 * Nothing here talks to the network or to React: it turns the development API's raw shapes into
 * the labels, tones, icons, links and plain-language copy the pages render. Keeping it pure keeps
 * the wording honest and testable — statuses are never signaled by color alone, and the streak
 * copy never pressures anyone.
 */
import type { Recommendation, RecommendationKind, SkillMasteryStatus, SkillMatrixRow, StageStatus } from '@worksim/engine';
import type { IconName } from '../components/ui/Icon';
import type { Route } from '../router';

/* ------------------------------------------------------------------ skills */

export type StatusTone = 'neutral' | 'accent' | 'cyan' | 'violet' | 'warn' | 'ok';

export interface StatusMeta {
  /** Sentence-case label shown to people. */
  label: string;
  /** Badge tone class suffix; never the only signal — the label and icon carry the meaning too. */
  tone: StatusTone;
  icon: IconName;
  /** How this status is reached, for the "How statuses are decided" disclosure. */
  rule: string;
}

/** Order used for counts, filters and the mini bar, weakest first. */
export const SKILL_STATUS_ORDER: SkillMasteryStatus[] = ['not-started', 'learning', 'introduced', 'practicing', 'needs-review', 'demonstrated', 'strong-evidence'];

const STATUS_META: Record<SkillMasteryStatus, StatusMeta> = {
  'not-started': { label: 'Not started', tone: 'neutral', icon: 'clock', rule: 'Nothing recorded for this skill yet.' },
  learning: { label: 'Learning', tone: 'accent', icon: 'lightbulb', rule: 'You started a lesson on this skill but have not passed it or its knowledge check yet.' },
  introduced: { label: 'Introduced', tone: 'accent', icon: 'book', rule: 'A lesson covering the skill was passed, or evidence carried over from another track. Reading about a skill is not practicing it.' },
  practicing: { label: 'Practicing', tone: 'cyan', icon: 'target', rule: 'You have attempted the skill but no attempt has scored 70% or better yet.' },
  'needs-review': { label: 'Needs review', tone: 'warn', icon: 'warning', rule: 'You demonstrated it before, but the newest evidence is over 90 days old or your latest attempt fell below 50%.' },
  demonstrated: { label: 'Demonstrated', tone: 'ok', icon: 'check', rule: 'At least one attempt in the last 90 days scored 70% or better.' },
  'strong-evidence': { label: 'Strong evidence', tone: 'violet', icon: 'star', rule: 'Three recent attempts at 70% or better, or two different kinds of work (a lab and a scenario, say) within the last 90 days.' },
};

export const statusMeta = (status: SkillMasteryStatus): StatusMeta => STATUS_META[status] ?? STATUS_META['not-started'];
export const statusLabel = (status: SkillMasteryStatus): string => statusMeta(status).label;
export const statusTone = (status: SkillMasteryStatus): StatusTone => statusMeta(status).tone;
export const statusIcon = (status: SkillMasteryStatus): IconName => statusMeta(status).icon;

/** Badge class for a status. The label always travels with it, so color is never alone. */
export const statusBadgeClass = (status: SkillMasteryStatus): string => {
  const tone = statusTone(status);
  return tone === 'neutral' ? 'badge' : `badge ${tone}`;
};

/** Every status with its rule, in the order the disclosure lists them. */
export const statusRules = (): { status: SkillMasteryStatus; meta: StatusMeta }[] =>
  [...SKILL_STATUS_ORDER].reverse().map((status) => ({ status, meta: statusMeta(status) }));

/** Counts in display order, with the share each one takes of the total (for the mini bar). */
export function statusBreakdown(counts: Partial<Record<SkillMasteryStatus, number>>): { status: SkillMasteryStatus; count: number; share: number; meta: StatusMeta }[] {
  const total = SKILL_STATUS_ORDER.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
  return SKILL_STATUS_ORDER.map((status) => {
    const count = counts[status] ?? 0;
    return { status, count, share: total > 0 ? count / total : 0, meta: statusMeta(status) };
  });
}

/** Rows a filter pair keeps. Empty filters keep everything. */
export function filterSkillRows(rows: SkillMatrixRow[], statuses: SkillMasteryStatus[], tiers: string[]): SkillMatrixRow[] {
  return rows.filter((r) => (statuses.length === 0 || statuses.includes(r.status)) && (tiers.length === 0 || tiers.includes(String(r.tier))));
}

/* ------------------------------------------------------------------ stages */

export interface StageProgress {
  done: number;
  total: number;
  /** 0..1 across every requirement of the stage. */
  ratio: number;
  met: boolean;
}

/** How far one stage is, counting each requirement's own done/total. */
export function stageProgress(stage: StageStatus): StageProgress {
  const done = stage.requirements.reduce((sum, r) => sum + Math.min(r.done, r.total), 0);
  const total = stage.requirements.reduce((sum, r) => sum + r.total, 0);
  return { done, total, ratio: total > 0 ? Math.min(1, done / total) : 0, met: stage.requirements.length > 0 && stage.requirements.every((r) => r.met) };
}

export type StageState = 'reached' | 'current' | 'ahead';

/** Reached, current or still ahead — the three states the stepper renders. */
export const stageState = (stage: StageStatus): StageState => (stage.reached ? 'reached' : stage.current ? 'current' : 'ahead');

/** Stages reached out of all of them, for the journey summary. */
export function journeyProgress(stages: StageStatus[]): { reached: number; total: number; ratio: number } {
  const reached = stages.filter((s) => s.reached).length;
  return { reached, total: stages.length, ratio: stages.length > 0 ? reached / stages.length : 0 };
}

/* ------------------------------------------------------------------ streak */

/**
 * Neutral streak copy. A streak here records days you studied; it is never framed as something
 * to protect, and a gap is described without blame.
 */
export function streakCopy(streak: { current: number; longest: number; lastDay?: string }): { headline: string; detail: string } {
  const longest = streak.longest > 0 ? `Your longest run so far is ${streak.longest} ${streak.longest === 1 ? 'day' : 'days'}.` : '';
  if (streak.current <= 0) {
    return {
      headline: 'No study days in a row right now',
      detail: streak.longest > 0 ? `${longest} Picking up again today starts a new one — nothing you have done is lost.` : 'Study on any day and it is recorded here. Nothing expires.',
    };
  }
  return {
    headline: `${streak.current}-day streak`,
    detail: `You studied on ${streak.current} ${streak.current === 1 ? 'day' : 'days'} in a row. ${longest}`.trim(),
  };
}

/* --------------------------------------------------------- recommendations */

export interface RecommendationMeta {
  label: string;
  icon: IconName;
  /** Which group the recommendation falls into on the dashboard. */
  group: 'Pick up where you left off' | 'Recall and review' | 'Learn something new' | 'Apply it';
  /** Verb for the button that opens it. */
  action: string;
}

const KIND_META: Record<RecommendationKind, RecommendationMeta> = {
  lesson: { label: 'Lesson', icon: 'book', group: 'Learn something new', action: 'Open the lesson' },
  review: { label: 'Review', icon: 'clock', group: 'Recall and review', action: 'Start the review' },
  'practice-quiz': { label: 'Practice questions', icon: 'clipboard', group: 'Recall and review', action: 'Practice now' },
  lab: { label: 'Lab', icon: 'terminal', group: 'Apply it', action: 'Open the lab' },
  project: { label: 'Project', icon: 'folder', group: 'Apply it', action: 'Open the project' },
  scenario: { label: 'Scenario', icon: 'briefcase', group: 'Apply it', action: 'Open the scenario' },
  interview: { label: 'Interview prep', icon: 'chat', group: 'Apply it', action: 'Open interview prep' },
  'certification-objective': { label: 'Certification objective', icon: 'shield', group: 'Learn something new', action: 'See the objective' },
  flashcards: { label: 'Flashcards', icon: 'sparkles', group: 'Recall and review', action: 'Open flashcards' },
  portfolio: { label: 'Portfolio', icon: 'star', group: 'Apply it', action: 'Open your portfolio' },
};

const FALLBACK_META: RecommendationMeta = { label: 'Next step', icon: 'target', group: 'Apply it', action: 'Open it' };

export const recommendationMeta = (kind: string): RecommendationMeta => KIND_META[kind as RecommendationKind] ?? FALLBACK_META;

/** A recommendation whose title already says "Resume:" is something already in flight. */
export const isResume = (r: Pick<Recommendation, 'title'>): boolean => /^resume:/i.test(r.title);

/** Where a recommendation goes. Everything routes to a page that actually exists. */
export function recommendationRoute(kind: string, id: string, careerId?: string): Route {
  switch (kind) {
    case 'lesson':
      return { page: 'learn-lesson', id };
    case 'lab':
      return { page: 'learn-lab', id };
    case 'scenario':
      return { page: 'scenario', id, career: careerId };
    case 'project':
      return { page: 'project', id };
    case 'interview':
      return { page: 'interview', career: careerId ?? id };
    case 'practice-quiz':
      return { page: 'prep-bank', id };
    case 'flashcards':
      return { page: 'prep-flashcards', id };
    case 'certification-objective':
      return { page: 'certification', id };
    case 'portfolio':
      return { page: 'deliverables' };
    case 'review':
    default:
      return { page: 'prep' };
  }
}

export interface RecommendationGroup {
  label: RecommendationMeta['group'];
  items: Recommendation[];
}

/** Recommendations bucketed by what kind of work they are, highest priority group first. */
export function groupRecommendations(recs: Recommendation[]): RecommendationGroup[] {
  const order: RecommendationMeta['group'][] = ['Pick up where you left off', 'Recall and review', 'Learn something new', 'Apply it'];
  const buckets = new Map<RecommendationMeta['group'], Recommendation[]>();
  for (const r of recs) {
    const group = isResume(r) ? 'Pick up where you left off' : recommendationMeta(r.kind).group;
    const list = buckets.get(group) ?? [];
    list.push(r);
    buckets.set(group, list);
  }
  return order.filter((g) => (buckets.get(g)?.length ?? 0) > 0).map((label) => ({ label, items: buckets.get(label)! }));
}

/** "About 20 minutes" / "" — minutes are an estimate, so the copy says so. */
export function minutesCopy(minutes?: number): string {
  if (minutes === undefined || minutes <= 0) return '';
  if (minutes < 60) return `About ${Math.round(minutes)} min`;
  const hours = minutes / 60;
  const rounded = Math.round(hours * 10) / 10;
  return `About ${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} ${rounded === 1 ? 'hour' : 'hours'}`;
}

/* -------------------------------------------- stage requirement signposting */

export interface RequirementLink {
  label: string;
  route: Route;
}

/**
 * What satisfies a stage requirement. Ids come from `computeStages` in the engine; anything
 * unrecognized falls back to the learning path, which is where most work is queued.
 */
export function requirementLink(requirementId: string, careerId: string): RequirementLink {
  switch (requirementId) {
    case 'track':
      return { label: 'Finish onboarding', route: { page: 'onboarding' } };
    case 'lessons-3':
      return { label: 'Open your learning path', route: { page: 'learn-path', id: careerId } };
    case 'labs-2':
    case 'challenge-2':
      return { label: 'Browse the labs', route: { page: 'learn' } };
    case 'intro':
    case 'core-2':
    case 'cross':
    case 'path-scenarios':
      return { label: 'See your assignments', route: { page: 'assignments' } };
    case 'skills':
      return { label: 'See which skills need evidence', route: { page: 'progress', tab: 'skills' } };
    case 'project':
      return { label: 'Browse projects', route: { page: 'projects', career: careerId } };
    case 'portfolio':
      return { label: 'Package portfolio evidence', route: { page: 'deliverables' } };
    case 'interview':
      return { label: 'Open interview prep', route: { page: 'interview', career: careerId } };
    case 'practice':
      return { label: 'Open certification prep', route: { page: 'prep' } };
    case 'path-half':
    default:
      return { label: 'Open your learning path', route: { page: 'learn-path', id: careerId } };
  }
}

/* -------------------------------------------------------------- enrollments */

export type EnrollmentStatus = 'active' | 'paused' | 'completed';

export const ENROLLMENT_META: Record<EnrollmentStatus, { label: string; tone: StatusTone; icon: IconName }> = {
  active: { label: 'Active', tone: 'ok', icon: 'play' },
  paused: { label: 'Paused', tone: 'warn', icon: 'clock' },
  completed: { label: 'Completed', tone: 'accent', icon: 'flag' },
};

export const enrollmentMeta = (status: string) => ENROLLMENT_META[status as EnrollmentStatus] ?? ENROLLMENT_META.active;

/* ----------------------------------------------------- certification labels */

/** Readiness labels carry a tone, but the label text is always shown beside it. */
export function readinessTone(label: string): StatusTone {
  switch (label) {
    case 'strong practice':
      return 'violet';
    case 'practicing broadly':
      return 'ok';
    case 'developing':
      return 'cyan';
    case 'building foundations':
      return 'warn';
    default:
      return 'neutral';
  }
}

/** A share as a whole-number percentage, clamped. */
export const percent = (value: number): number => Math.round(Math.max(0, Math.min(1, value)) * 100);
